import { clsx, type ClassValue } from 'clsx';
import { twMerge }               from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number | string, currency = 'PEN'): string {
  return new Intl.NumberFormat('es-PE', { style: 'currency', currency, minimumFractionDigits: 2 }).format(Number(value));
}

/**
 * Parsea una fecha evitando el desfase UTC.
 * Las cadenas YYYY-MM-DD (sin hora) se tratan como fecha local de Perú,
 * no como medianoche UTC (que en UTC-5 retrocede al día anterior).
 */
export function parseLocalDate(date: string | Date): Date {
  if (date instanceof Date) return date;
  // Extract YYYY-MM-DD from ISO datetime strings (e.g. "2026-01-01T00:00:00.000Z")
  const datePart = /^(\d{4}-\d{2}-\d{2})/.exec(date)?.[1] ?? date;
  const [y, m, d] = datePart.split('-').map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return new Date(date);
}

export function formatDate(date: string | Date, opts?: Intl.DateTimeFormatOptions): string {
  let d: Date;
  if (typeof date === 'string') {
    // Ancla la fecha en Lima noon (UTC-5 → 17:00 UTC) para que Intl.DateTimeFormat
    // con timeZone 'America/Lima' siempre devuelva el mismo día independientemente
    // de la zona horaria del navegador del usuario.
    const datePart = /^(\d{4}-\d{2}-\d{2})/.exec(date)?.[1] ?? date;
    const [y, m, dy] = datePart.split('-').map(Number);
    d = (y && m && dy)
      ? new Date(Date.UTC(y, m - 1, dy, 17, 0, 0))
      : new Date(date);
  } else {
    d = date;
  }
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...opts,
  }).format(d);
}

/**
 * Formatea un timestamp REAL (con hora, ej. createdAt/fechaEnvio) convertido
 * a la zona horaria de Lima — a diferencia de `formatDate`, que asume que la
 * cadena es una fecha-sin-hora y la ancla a mediodía. Usar `formatDate` para
 * vencimientos/fechas de declaración (sin hora real); usar esta función para
 * cualquier campo que sí tenga una hora real (se envió, se creó, se actualizó).
 */
export function formatDateTimeLima(date: string | Date, opts?: Intl.DateTimeFormatOptions): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...opts,
  }).format(d);
}

export function formatPeriodo(periodo: string): string {
  if (periodo.length === 7) {
    const [y, m] = periodo.split('-');
    const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return `${months[Number(m) - 1] ?? m} ${y}`;
  }
  return periodo;
}

/**
 * Devuelve la fecha "ahora" en la zona horaria de Perú (America/Lima, UTC-5),
 * independientemente de la zona horaria del dispositivo del usuario.
 * Para uso en defaults de filtros y construcción de calendarios SUNAT.
 */
export function nowPeru(): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Lima',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const v = (t: string) => Number(parts.find(p => p.type === t)?.value ?? 0);
  return new Date(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second'));
}

/** Periodo tributario YYYYMM correspondiente a "hoy" en Perú. */
export function currentPeriodoPeru(): string {
  const d = nowPeru();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Convierte una fecha (string ISO o Date) al periodo tributario YYYYMM.
 * Usa parseLocalDate para evitar desfase UTC sobre fechas YYYY-MM-DD.
 */
export function periodoFromFecha(fecha: string | Date): string {
  const d = parseLocalDate(fecha);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function scoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

export function scoreBg(score: number): string {
  if (score >= 80) return 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-800';
  if (score >= 60) return 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800';
  return 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800';
}

export function debtAgeColor(fechaDeuda: string | Date): string {
  const days = (Date.now() - parseLocalDate(fechaDeuda).getTime()) / 86_400_000;
  if (days < 30)  return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
  if (days < 90)  return 'bg-amber-100  text-amber-800  dark:bg-amber-900  dark:text-amber-200';
  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
}

export function initials(name: string): string {
  return name.split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();
}
